import { useCallback, useEffect, useMemo, useState } from 'react';
import { Share } from '@capacitor/share';
import {
  Box,
  Button,
  Card,
  Icon,
  Link,
  Spinner,
  Tag,
  Text,
  Title,
} from '@nimbus-ds/components';
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ExclamationCircleIcon,
  PrinterIcon,
} from '@nimbus-ds/icons';
import {
  InformationForContentDeclarationResponseDto,
  InformationForDocumentResponseDto,
  NuvemEnvioErroredLabelDto,
  NuvemEnvioReadyLabelDto,
} from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { useToast } from 'commons/hooks';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import useDownloadNuvemEnvioLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useDownloadNuvemEnvioLabel/useDownloadNuvemEnvioLabel';
import { InformationForDocumentAndContentDeclaration } from 'domains/NuvemEnvioCommons/Orders/ordersSlice/types/information.type';
import { trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsClick } from 'domains/NuvemEnvioCommons/tracking';

interface PrintLabelResultProps {
  type: 'success' | 'error';
  labels?: NuvemEnvioReadyLabelDto[] | null;
  returnPath?: string;
  orderId?: string;
  isConcatenationInProgress?: boolean;
  information?:
    | InformationForContentDeclarationResponseDto[]
    | InformationForDocumentResponseDto[]
    | undefined;
  erroredLabels?: NuvemEnvioErroredLabelDto[] | null;
}

function PrintLabelResult({
  type,
  labels,
  returnPath,
  orderId,
  isConcatenationInProgress,
  information,
  erroredLabels,
}: Readonly<PrintLabelResultProps>) {
  const t = useNuvemEnvioTranslations();
  const isMobile = useIsMobileDevice();
  const { goTo } = useNavegate();
  const { addToast } = useToast();
  const [isPrinting, setIsPrinting] = useState(false);

  const {
    downloadNuvemEnvioLabel,
    data: downloadNuvemEnvioLabelData,
    status: downloadNuvemEnvioLabelStatus,
  } = useDownloadNuvemEnvioLabel();

  const handlePrintLabel = useCallback(async () => {
    try {
      setIsPrinting(true);
      trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsClick(
        orderId as string,
      );
      await downloadNuvemEnvioLabel({
        labels: labels as NuvemEnvioReadyLabelDto[],
      });
    } catch (error) {
      addToast({
        label: t(
          'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.toast.error',
        ),
        appearance: 'danger',
      });
    } finally {
      setIsPrinting(false);
    }
  }, [downloadNuvemEnvioLabel, labels, addToast, t, orderId]);

  const infoArray: InformationForDocumentAndContentDeclaration[] | undefined =
    useMemo(
      () => (Array.isArray(information) ? information : undefined),
      [information],
    );

  const ordersCanBePrinted = useMemo(
    () =>
      labels?.flatMap((label) => {
        if (!infoArray) {
          return [];
        }

        const currentOrder = infoArray.find(
          (p) => p.fulfillmentOrder.id === label.fulfillmentOrderId,
        );

        return currentOrder
          ? [{ orderNumber: currentOrder?.order?.number }]
          : [];
      }),
    [labels, infoArray],
  );

  const ordersWithError = useMemo(() => {
    if (!erroredLabels?.length) return [];
    if (!infoArray) return [];

    const withError = infoArray.filter((p) =>
      erroredLabels.some(
        (label) => label.fulfillmentOrderId === p.fulfillmentOrder.id,
      ),
    );

    if (!withError.length) return [];

    return withError.map((p) => ({
      fulfillmentOrderId: p.fulfillmentOrder.id,
      orderNumber: p.order.number,
    }));
  }, [erroredLabels, infoArray]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        handlePrintLabel();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePrintLabel, labels]);

  useEffect(() => {
    if (isMobile) {
      Share.share({ url: downloadNuvemEnvioLabelData.documentUrl });
    }
  }, [isMobile, downloadNuvemEnvioLabelData]);

  const config = {
    success: {
      icon: <CheckCircleIcon size="medium" color="green" />,
      title: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.title',
      ),
      buttonText: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.button',
      ),
      buttonIcon: <PrinterIcon />,
      onAction: () => handlePrintLabel(),
      appearance: 'primary' as const,
      dataTestId: 'print-async-nuvem-envio-label-button',
      cancelButtonDataTestId: 'print-async-nuvem-envio-label-cancel-button',
      cancelButtonText: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.cancel',
      ),
      onActionCancel: () => goTo(returnPath as string),
      appearanceCancel: 'neutral' as const,
    },
    error: {
      icon: <ExclamationCircleIcon size="medium" color="red" />,
      title: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.error.title',
      ),
      buttonText: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.error.button',
      ),
      buttonIcon: <ChevronLeftIcon />,
      onAction: () => goTo(returnPath as string),
      appearance: 'neutral' as const,
      dataTestId: 'print-async-nuvem-envio-label-error-link',
      textDecoration: 'none' as const,
    },
  }[type];

  return (
    <Card>
      <Box
        display="flex"
        flexDirection="column"
        gap={{ xs: '4', md: '8' }}
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center" gap="2">
          {config.icon}
          <Title as="h5">{config.title}</Title>
        </Box>
        {type === 'success' && information && information?.length > 1 && (
          <Box display="flex" gap="2" flexWrap="wrap">
            {ordersCanBePrinted
              ?.filter((label) => label.orderNumber !== null)
              .map((label) => (
                <Tag key={label.orderNumber}>#{label.orderNumber}</Tag>
              ))}
          </Box>
        )}
        {type === 'error' && information && information?.length > 1 && (
          <Box display="flex" gap="2" flexWrap="wrap">
            {ordersWithError?.map((label) => (
              <Tag key={label.fulfillmentOrderId} appearance="danger">
                #{label.orderNumber}
              </Tag>
            ))}
          </Box>
        )}

        {type === 'success' ? (
          <Box display="flex" gap="2">
            <Button
              appearance={config.appearanceCancel}
              onClick={config.onActionCancel}
              data-testid={config.cancelButtonDataTestId}
            >
              {config.cancelButtonText}
            </Button>
            <Button
              appearance={config.appearance}
              onClick={config.onAction}
              data-testid={config.dataTestId}
              disabled={isPrinting || isConcatenationInProgress}
            >
              {downloadNuvemEnvioLabelStatus.isLoading ||
              isConcatenationInProgress ? (
                <Spinner size="small" color="currentColor" />
              ) : (
                config.buttonIcon
              )}
              {config.buttonText}
            </Button>
          </Box>
        ) : (
          information?.length === 1 && (
            <Link
              appearance={config.appearance}
              onClick={config.onAction}
              textDecoration={config.textDecoration}
              data-testid={config.dataTestId}
            >
              <Icon color="primary-interactive" source={config.buttonIcon} />
              <Text color="primary-interactive">{config.buttonText}</Text>
            </Link>
          )
        )}
      </Box>
    </Card>
  );
}

export default PrintLabelResult;
